import { normalizeComplianceStandardCode } from './complianceStandard'
import type { HomeSupplierRecord } from '../components/home/HomeSuppliersExecutiveCard'

export type SupplierStandardComplianceRow = {
  code: string
  percent: number | null
}

function parseAssignedCodes(standards: HomeSupplierRecord['standards']): string[] {
  if (!Array.isArray(standards)) return []
  const codes: string[] = []
  for (const item of standards) {
    if (typeof item === 'string') {
      const code = normalizeComplianceStandardCode(item)
      if (code && code !== 'ALL') codes.push(code)
      continue
    }
    if (item && typeof item === 'object') {
      const raw = (item as { code?: string; standardCode?: string }).code
        ?? (item as { standardCode?: string }).standardCode
      const code = normalizeComplianceStandardCode(raw)
      if (code && code !== 'ALL') codes.push(code)
    }
  }
  return Array.from(new Set(codes))
}

export function supplierOverallPercent(row: HomeSupplierRecord): number {
  const v =
    row.compliancePercentage
    ?? row.compliancePct
    ?? row.percent
    ?? (row as { compliance?: number }).compliance
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n) : 0
}

/** Per-standard rows: API standardsCompliance, or assigned codes with optional scores. */
export function getSupplierStandardRows(
  supplier: HomeSupplierRecord,
): SupplierStandardComplianceRow[] {
  const fromApi = supplier.standardsCompliance
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi
      .map((row) => ({
        code: normalizeComplianceStandardCode(row.code),
        percent:
          row.percent == null || !Number.isFinite(Number(row.percent))
            ? null
            : Math.round(Number(row.percent)),
      }))
      .filter((row) => row.code && row.code !== 'ALL')
  }

  const assigned = parseAssignedCodes(supplier.standards)
  const overall = supplierOverallPercent(supplier)
  return assigned.map((code) => ({ code, percent: overall }))
}

/** When more than two suppliers: average compliance per assigned standard. */
export function aggregateSupplierStandardsByCode(
  suppliers: HomeSupplierRecord[],
): SupplierStandardComplianceRow[] {
  const buckets = new Map<string, { sum: number; count: number }>()

  for (const supplier of suppliers) {
    for (const row of getSupplierStandardRows(supplier)) {
      const pct = row.percent
      if (pct == null || !Number.isFinite(pct)) continue
      const cur = buckets.get(row.code) || { sum: 0, count: 0 }
      cur.sum += pct
      cur.count += 1
      buckets.set(row.code, cur)
    }
  }

  return Array.from(buckets.entries())
    .map(([code, { sum, count }]) => ({
      code,
      percent: count > 0 ? Math.round(sum / count) : 0,
    }))
    .sort((a, b) => a.code.localeCompare(b.code))
}
